import * as React from "react";

import {
  StyledTable,
  StyledTh,
  StyledTd,
  StyledTr,
  CheckboxWrapper,
  StyledTbody,
} from "./styles";
import Checkbox from "../Checkbox";
import { handleSelectAll } from "./utils";

type Column = {
  label: string;
  accessor: string;
  renderCell?: (row: any, index?: number) => React.ReactNode;
};

type TableProps = {
  columns: Array<Column>;
  data: Array<{ id?: number }>;
  selectable?: boolean;
  isDisabled?: boolean;
  selectedRows?: Set<number>;
  onRowSelect?: (rowId: number) => void;
};

const Table = ({
  columns,
  data,
  selectable = false,
  isDisabled = false,
  selectedRows = new Set(),
  onRowSelect,
}: TableProps) => {
  const allSelected = React.useMemo(() => {
    return (
      data.length > 0 &&
      data.every((row) => selectedRows.has(row.id || data.indexOf(row)))
    );
  }, [data, selectedRows]);

  const someSelected = React.useMemo(() => {
    return data.some((row) => selectedRows.has(row.id || data.indexOf(row)));
  }, [data, selectedRows]);

  return (
    <StyledTable>
      <thead>
        <StyledTr>
          {selectable && (
            <StyledTh isSelectColumn>
              <CheckboxWrapper>
                <Checkbox
                  checked={allSelected}
                  isDisabled={isDisabled}
                  indeterminate={!allSelected && someSelected}
                  onChange={() => {
                    if (onRowSelect) {
                      handleSelectAll(data, selectedRows, onRowSelect);
                    }
                  }}
                />
              </CheckboxWrapper>
            </StyledTh>
          )}
          {columns.map((col, id) => (
            <StyledTh key={col.accessor || id}>{col.label}</StyledTh>
          ))}
        </StyledTr>
      </thead>
      <StyledTbody>
        {data.map((row, rowIndex) => {
          const rowKey = row.id || rowIndex;
          const isSelected = selectedRows.has(rowKey);
          return (
            <StyledTr key={rowKey} isSelected={isSelected}>
              {selectable && (
                <StyledTd isSelectColumn>
                  <CheckboxWrapper>
                    <Checkbox
                      isDisabled={isDisabled}
                      checked={isSelected}
                      onChange={() => {
                        if (onRowSelect) {
                          onRowSelect(rowKey);
                        }
                      }}
                    />
                  </CheckboxWrapper>
                </StyledTd>
              )}
              {columns.map((col, colIndex) => (
                <StyledTd key={col.accessor || colIndex}>
                  {col.renderCell
                    ? col.renderCell(row, rowIndex)
                    : row[col.accessor as keyof typeof row]}
                </StyledTd>
              ))}
            </StyledTr>
          );
        })}
      </StyledTbody>
    </StyledTable>
  );
};

export default Table;
